import { settingsMessages } from "@/features/workspace/i18n/settings";
import { navMessages } from "./nav";

/**
 * THE WORDS OF ⌘K.
 *
 * The palette is the only surface where translating changes BEHAVIOUR and not
 * just appearance: what it draws is also what it searches. Whoever types
 * "invoice" in an English panel and "fatura" in a Portuguese one has to reach
 * the same screen, so the sieve compares the sentence in the ACTIVE language —
 * `nav-items.ts` hands over keys, the component resolves them, and only then
 * does `matches` run.
 *
 * ── WHY `terms` IS A TRANSLATED KEY LIKE THE REST ─────────────────────────
 *
 * Because synonyms are the most language-bound thing here: "boleto" has no
 * English equivalent and "chargeback" has no Portuguese one. A single list for
 * both languages would be a list that is wrong in one of them.
 *
 * ── THE FIVE BILLING ROWS ─────────────────────────────────────────────────
 *
 * `features/billing/sections.ts` is now a table of ADDRESSES: each row carries a
 * `key` and no sentence, and the four keys below are what the palette draws for
 * them — four and not five, because the root address is already a sidebar
 * destination above. They are not a copy of billing's own `nav.*`: a component
 * mounts ONE local i18n scope, this one is the palette's, and what it needs is
 * the prefixed reading plus the synonyms only a search box has any use for. A
 * SIXTH section born in that table needs a name here too; without one the palette
 * draws it blank.
 */
export const paletteMessages = {
	en: {
		palette: {
			placeholder: "Go to, search or create…",
			empty: "Nothing matches",
			searching: "searching…",
			emptyHint: "Try another word, or one of these",
			suggestions: "Try",
			footer: {
				move: "move",
				open: "open",
				close: "close",
				/** O tempo de execução no canto direito, como a V2 desenhava. */
				took: "{ms}ms",
			},
			terms: {
				screen: "screen view board built custom",
				funnel: "funnel board kanban pipeline stages cards",
			},
			/** `{shown} of {total}` — the denominator the cut used to hide. */
			counted: "{shown} of {total}",
			more: "see {n} more",
			partial: "Searched {searched} of {active} data tables in this workspace.",
			/** O que cada peça de "Seu sistema" é, na segunda linha do resultado. */
			piece: {
				entity: "Data table",
				field: "Field",
				view: "Screen",
				component: "Custom screen",
				pipeline: "Funnel",
				artifact: "Function",
				automation: "Automation",
				report: "Report",
				agent: "Attendant",
				of: "{kind} · {owner}",
				archived: "{name} · archived",
			},
			sections: {
				product: "Product screens",
				screens: "Custom screens",
				funnels: "Funnels",
				entities: "Data tables",
				pieces: "In your system",
				admin: "Administration",
				contact: "Contacts",
				conversation: "Conversations",
				record: "Records",
				action: "Create without leaving",
			},
			actions: {
				contact: { label: "+ contact", hint: "create a person" },
				pipeline: { label: "+ funnel", hint: "create a funnel" },
				conversation: { label: "+ conversation", hint: "reach out first" },
				agent: { label: "+ agent", hint: "create an agent" },
			},
			prefix: {
				billing: "Billing",
				settings: "Settings",
				system: "Your system",
			},
			/**
			 * AS DUAS ABAS DE "Seu sistema" que têm endereço próprio.
			 *
			 * Elas existem no `?aba=` desde sempre e não existiam em porta
			 * nenhuma: quem procura "histórico" não o achava, e o Ben só sabia
			 * levar a pessoa à primeira aba — dizia "liga em O que pode ter" e
			 * não tinha como abrir. A terceira (o que você TEM) fica de fora: é
			 * a que `/system` já abre.
			 */
			system: {
				could: { label: "What you could have", hint: "add-ons and packages, one click each" },
				history: { label: "History", hint: "every change, with undo" },
			},
			billing: {
				credits: {
					label: "Credits",
					hint: "how much AI is left",
					terms:
						"credits balance quota bonus pack buy credits top up ai blocked no balance " +
						"how much is left cycle reset",
				},
				limits: {
					label: "Limits",
					hint: "users, channels, GB, contacts",
					terms:
						"limit limits users channels storage gb space active contacts add-on add-ons " +
						"buy more users raise limit",
				},
				invoices: {
					label: "Invoices",
					hint: "what was charged, month by month",
					terms: "invoice invoices bill receipt payment history due date paid pending overdue",
				},
				consumption: {
					label: "Consumption",
					hint: "where the credits went",
					terms:
						"consumption statement usage of calls by origin errors average duration " +
						"where did i spend credits consumption history",
				},
			},
		},
	},
	"pt-BR": {
		palette: {
			placeholder: "Ir para, buscar ou criar…",
			empty: "Nada corresponde",
			searching: "procurando…",
			emptyHint: "Tente outra palavra, ou uma destas",
			suggestions: "Tente",
			footer: {
				move: "navegar",
				open: "abrir",
				close: "fechar",
				/** O tempo de execução no canto direito, como a V2 desenhava. */
				took: "{ms}ms",
			},
			terms: {
				screen: "tela visao quadro construida",
				funnel: "funil quadro board kanban etapas cards",
			},
			counted: "{shown} de {total}",
			more: "ver mais {n}",
			partial: "Procurou em {searched} de {active} tabelas de dados deste workspace.",
			/** O que cada peça de "Seu sistema" é, na segunda linha do resultado. */
			piece: {
				entity: "Tabela de dados",
				field: "Campo",
				view: "Tela",
				component: "Tela sob medida",
				pipeline: "Funil",
				artifact: "Função",
				automation: "Automação",
				report: "Relatório",
				agent: "Atendente",
				of: "{kind} · {owner}",
				archived: "{name} · arquivada",
			},
			sections: {
				product: "Telas do produto",
				screens: "Telas sob medida",
				funnels: "Funis",
				entities: "Tabelas de dados",
				pieces: "No seu sistema",
				admin: "Administração",
				contact: "Contatos",
				conversation: "Conversas",
				record: "Registros",
				action: "Criar sem sair daqui",
			},
			actions: {
				contact: { label: "+ contato", hint: "criar uma pessoa" },
				pipeline: { label: "+ funil", hint: "criar um funil" },
				conversation: { label: "+ conversa", hint: "falar com alguém primeiro" },
				agent: { label: "+ agente", hint: "criar um agente" },
			},
			prefix: {
				billing: "Cobrança",
				settings: "Configurações",
				system: "Seu sistema",
			},
			/**
			 * AS DUAS ABAS DE "Seu sistema" que têm endereço próprio.
			 *
			 * Elas existem no `?aba=` desde sempre e não existiam em porta
			 * nenhuma: quem procura "histórico" não o achava, e o Ben só sabia
			 * levar a pessoa à primeira aba — dizia "liga em O que pode ter" e
			 * não tinha como abrir. A terceira (o que você TEM) fica de fora: é
			 * a que `/system` já abre.
			 */
			system: {
				could: { label: "O que pode ter", hint: "complementos e pacotes, um clique cada" },
				history: { label: "Histórico", hint: "cada mudança, com desfazer" },
			},
			billing: {
				credits: {
					label: "Créditos",
					hint: "quanto de IA ainda tem",
					terms:
						"creditos saldo cota bonus pacote comprar creditos recarga ia bloqueada sem saldo " +
						"quanto sobrou reset do ciclo",
				},
				limits: {
					label: "Limites",
					hint: "usuários, canais, GB, contatos",
					terms:
						"limite limites usuarios canais armazenamento gb espaco contatos ativos adicional " +
						"adicionais comprar mais usuarios aumentar limite",
				},
				invoices: {
					label: "Faturas",
					hint: "o que foi cobrado, mês a mês",
					terms:
						"fatura faturas nota boleto recibo comprovante historico de pagamento vencimento " +
						"pagou pendente vencida",
				},
				consumption: {
					label: "Consumo",
					hint: "onde os créditos foram",
					terms:
						"consumo extrato uso das chamadas por origem erros duracao media onde gastei " +
						"creditos historico de consumo",
				},
			},
		},
	},
	el: {
		palette: {
			placeholder: "Μετάβαση, αναζήτηση ή δημιουργία…",
			empty: "Καμία αντιστοιχία",
			searching: "αναζήτηση…",
			emptyHint: "Δοκίμασε άλλη λέξη, ή μία από αυτές",
			suggestions: "Δοκίμασε",
			footer: {
				move: "μετακίνηση",
				open: "άνοιγμα",
				close: "κλείσιμο",
				/** O tempo de execução no canto direito, como a V2 desenhava. */
				took: "{ms}ms",
			},
			terms: {
				screen: "οθόνη προβολή πίνακας",
				funnel: "χοάνη πίνακας kanban στάδια κάρτες",
			},
			counted: "{shown} από {total}",
			more: "δες {n} ακόμα",
			partial:
				"Έγινε αναζήτηση σε {searched} από {active} πίνακες δεδομένων αυτού του χώρου εργασίας.",
			/** O que cada peça de "Seu sistema" é, na segunda linha do resultado. */
			piece: {
				entity: "Πίνακας δεδομένων",
				field: "Πεδίο",
				view: "Οθόνη",
				component: "Οθόνη κατά παραγγελία",
				pipeline: "Χωνί",
				artifact: "Συνάρτηση",
				automation: "Αυτοματισμός",
				report: "Αναφορά",
				agent: "Εκπρόσωπος",
				of: "{kind} · {owner}",
				archived: "{name} · αρχειοθετημένος",
			},
			sections: {
				product: "Οθόνες του προϊόντος",
				screens: "Οθόνες κατά παραγγελία",
				funnels: "Χοάνες",
				entities: "Πίνακες δεδομένων",
				pieces: "Στο σύστημά σου",
				admin: "Διαχείριση",
				contact: "Επαφές",
				conversation: "Συνομιλίες",
				record: "Εγγραφές",
				action: "Δημιουργία χωρίς να φύγεις από εδώ",
			},
			actions: {
				contact: { label: "+ επαφή", hint: "δημιουργία ατόμου" },
				pipeline: { label: "+ χοάνη", hint: "δημιουργία χοάνης" },
				conversation: { label: "+ συνομιλία", hint: "ξεκίνα εσύ τη συνομιλία" },
				agent: { label: "+ πράκτορας", hint: "δημιουργία πράκτορα" },
			},
			prefix: {
				billing: "Χρεώσεις",
				settings: "Ρυθμίσεις",
				system: "Το σύστημά σου",
			},
			/**
			 * AS DUAS ABAS DE "Seu sistema" que têm endereço próprio.
			 *
			 * Elas existem no `?aba=` desde sempre e não existiam em porta
			 * nenhuma: quem procura "histórico" não o achava, e o Ben só sabia
			 * levar a pessoa à primeira aba — dizia "liga em O que pode ter" e
			 * não tinha como abrir. A terceira (o que você TEM) fica de fora: é
			 * a que `/system` já abre.
			 */
			system: {
				could: { label: "Τι μπορείς να έχεις", hint: "πρόσθετα και πακέτα, ένα κλικ το καθένα" },
				history: { label: "Ιστορικό", hint: "κάθε αλλαγή, με αναίρεση" },
			},
			billing: {
				credits: {
					label: "Πιστώσεις",
					hint: "πόσο AI απομένει",
					terms:
						"πιστώσεις υπόλοιπο όριο δώρο πακέτο αγορά πιστώσεων ανανέωση ai μπλοκαρισμένο " +
						"χωρίς υπόλοιπο πόσο απομένει μηδενισμός κύκλου",
				},
				limits: {
					label: "Όρια",
					hint: "χρήστες, κανάλια, GB, επαφές",
					terms:
						"όριο όρια χρήστες κανάλια αποθηκευτικός χώρος gb ενεργές επαφές πρόσθετο πρόσθετα " +
						"αγορά περισσότερων χρηστών αύξηση ορίου",
				},
				invoices: {
					label: "Τιμολόγια",
					hint: "τι χρεώθηκε, μήνα με μήνα",
					terms:
						"τιμολόγιο τιμολόγια λογαριασμός απόδειξη ιστορικό πληρωμών ημερομηνία λήξης " +
						"πληρωμένο σε εκκρεμότητα ληξιπρόθεσμο",
				},
				consumption: {
					label: "Κατανάλωση",
					hint: "πού πήγαν οι πιστώσεις",
					terms:
						"κατανάλωση αναλυτικό χρήση κλήσεων ανά προέλευση σφάλματα μέση διάρκεια " +
						"πού ξόδεψα πιστώσεις ιστορικό κατανάλωσης",
				},
			},
		},
	},
	es: {
		palette: {
			placeholder: "Ir a, buscar o crear…",
			empty: "Nada coincide",
			searching: "buscando…",
			emptyHint: "Prueba otra palabra, o una de estas",
			suggestions: "Prueba",
			footer: {
				move: "mover",
				open: "abrir",
				close: "cerrar",
				/** O tempo de execução no canto direito, como a V2 desenhava. */
				took: "{ms}ms",
			},
			terms: {
				screen: "pantalla vista tablero creada",
				funnel: "embudo tablero kanban etapas tarjetas",
			},
			counted: "{shown} de {total}",
			more: "ver {n} más",
			partial: "Se buscó en {searched} de {active} tablas de datos de este espacio de trabajo.",
			/** O que cada peça de "Seu sistema" é, na segunda linha do resultado. */
			piece: {
				entity: "Tabla de datos",
				field: "Campo",
				view: "Pantalla",
				component: "Pantalla a medida",
				pipeline: "Embudo",
				artifact: "Función",
				automation: "Automatización",
				report: "Informe",
				agent: "Agente",
				of: "{kind} · {owner}",
				archived: "{name} · archivada",
			},
			sections: {
				product: "Pantallas del producto",
				screens: "Pantallas a medida",
				funnels: "Embudos",
				entities: "Tablas de datos",
				pieces: "En tu sistema",
				admin: "Administración",
				contact: "Contactos",
				conversation: "Conversaciones",
				record: "Registros",
				action: "Crear sin salir de aquí",
			},
			actions: {
				contact: { label: "+ contacto", hint: "crear una persona" },
				pipeline: { label: "+ embudo", hint: "crear un embudo" },
				conversation: { label: "+ conversación", hint: "escribir tú primero" },
				agent: { label: "+ agente", hint: "crear un agente" },
			},
			prefix: {
				billing: "Facturación",
				settings: "Ajustes",
				system: "Tu sistema",
			},
			/**
			 * AS DUAS ABAS DE "Seu sistema" que têm endereço próprio.
			 *
			 * Elas existem no `?aba=` desde sempre e não existiam em porta
			 * nenhuma: quem procura "histórico" não o achava, e o Ben só sabia
			 * levar a pessoa à primeira aba — dizia "liga em O que pode ter" e
			 * não tinha como abrir. A terceira (o que você TEM) fica de fora: é
			 * a que `/system` já abre.
			 */
			system: {
				could: { label: "Lo que puedes tener", hint: "complementos y paquetes, un clic cada uno" },
				history: { label: "Historial", hint: "cada cambio, con deshacer" },
			},
			billing: {
				credits: {
					label: "Créditos",
					hint: "cuánta IA queda",
					terms:
						"creditos saldo cuota bonus paquete comprar creditos recarga ia bloqueada sin saldo " +
						"cuanto queda reinicio del ciclo",
				},
				limits: {
					label: "Límites",
					hint: "usuarios, canales, GB, contactos",
					terms:
						"limite limites usuarios canales almacenamiento gb espacio contactos activos " +
						"complemento complementos comprar mas usuarios subir el limite",
				},
				invoices: {
					label: "Facturas",
					hint: "qué se cobró, mes a mes",
					terms:
						"factura facturas recibo justificante historial de pagos vencimiento pagada " +
						"pendiente vencida",
				},
				consumption: {
					label: "Consumo",
					hint: "adónde fueron los créditos",
					terms:
						"consumo extracto uso de llamadas por origen errores duracion media donde gaste " +
						"creditos historial de consumo",
				},
			},
		},
	},
	fr: {
		palette: {
			placeholder: "Aller à, rechercher ou créer…",
			empty: "Aucune correspondance",
			searching: "recherche…",
			emptyHint: "Essayez un autre mot, ou l'un de ceux-ci",
			suggestions: "Essayez",
			footer: {
				move: "naviguer",
				open: "ouvrir",
				close: "fermer",
				/** O tempo de execução no canto direito, como a V2 desenhava. */
				took: "{ms}ms",
			},
			terms: {
				screen: "ecran vue tableau construit",
				funnel: "pipeline tableau kanban etapes cartes",
			},
			counted: "{shown} sur {total}",
			more: "voir {n} de plus",
			partial:
				"Recherche faite dans {searched} des {active} tables de données de cet espace de travail.",
			/** O que cada peça de "Seu sistema" é, na segunda linha do resultado. */
			piece: {
				entity: "Table de données",
				field: "Champ",
				view: "Écran",
				component: "Écran sur mesure",
				pipeline: "Tunnel",
				artifact: "Fonction",
				automation: "Automatisation",
				report: "Rapport",
				agent: "Agent",
				of: "{kind} · {owner}",
				archived: "{name} · archivée",
			},
			sections: {
				product: "Écrans du produit",
				screens: "Écrans sur mesure",
				funnels: "Pipelines",
				entities: "Tables de données",
				pieces: "Dans votre système",
				admin: "Administration",
				contact: "Contacts",
				conversation: "Conversations",
				record: "Fiches",
				action: "Créer sans quitter l'écran",
			},
			actions: {
				contact: { label: "+ contact", hint: "créer une personne" },
				pipeline: { label: "+ pipeline", hint: "créer un pipeline" },
				conversation: { label: "+ conversation", hint: "écrire en premier" },
				agent: { label: "+ agent", hint: "créer un agent" },
			},
			prefix: {
				billing: "Facturation",
				settings: "Paramètres",
				system: "Votre système",
			},
			/**
			 * AS DUAS ABAS DE "Seu sistema" que têm endereço próprio.
			 *
			 * Elas existem no `?aba=` desde sempre e não existiam em porta
			 * nenhuma: quem procura "histórico" não o achava, e o Ben só sabia
			 * levar a pessoa à primeira aba — dizia "liga em O que pode ter" e
			 * não tinha como abrir. A terceira (o que você TEM) fica de fora: é
			 * a que `/system` já abre.
			 */
			system: {
				could: { label: "Ce que vous pouvez avoir", hint: "compléments et packs, un clic chacun" },
				history: { label: "Historique", hint: "chaque changement, avec annulation" },
			},
			billing: {
				credits: {
					label: "Crédits",
					hint: "combien d'IA il reste",
					terms:
						"credits solde quota bonus pack acheter des credits recharge ia bloquee sans solde " +
						"combien il reste remise a zero du cycle",
				},
				limits: {
					label: "Limites",
					hint: "utilisateurs, canaux, Go, contacts",
					terms:
						"limite limites utilisateurs canaux stockage go espace contacts actifs option " +
						"options supplementaires acheter plus d'utilisateurs augmenter la limite",
				},
				invoices: {
					label: "Factures",
					hint: "ce qui a été facturé, mois par mois",
					terms:
						"facture factures note recu historique de paiement echeance payee en attente impayee",
				},
				consumption: {
					label: "Consommation",
					hint: "où sont partis les crédits",
					terms:
						"consommation releve usage des appels par origine erreurs duree moyenne ou sont " +
						"passes mes credits historique de consommation",
				},
			},
		},
	},
	it: {
		palette: {
			placeholder: "Vai a, cerca o crea…",
			empty: "Nessuna corrispondenza",
			searching: "ricerca…",
			emptyHint: "Prova un'altra parola, o una di queste",
			suggestions: "Prova",
			footer: {
				move: "scorri",
				open: "apri",
				close: "chiudi",
				/** O tempo de execução no canto direito, como a V2 desenhava. */
				took: "{ms}ms",
			},
			terms: {
				screen: "schermata vista bacheca creata",
				funnel: "pipeline bacheca kanban fasi schede",
			},
			counted: "{shown} di {total}",
			more: "vedi altri {n}",
			partial: "Cercato in {searched} di {active} tabelle dati di questo spazio di lavoro.",
			/** O que cada peça de "Seu sistema" é, na segunda linha do resultado. */
			piece: {
				entity: "Tabella dati",
				field: "Campo",
				view: "Schermata",
				component: "Schermata su misura",
				pipeline: "Funnel",
				artifact: "Funzione",
				automation: "Automazione",
				report: "Report",
				agent: "Addetto",
				of: "{kind} · {owner}",
				archived: "{name} · archiviata",
			},
			sections: {
				product: "Schermate del prodotto",
				screens: "Schermate su misura",
				funnels: "Pipeline",
				entities: "Tabelle dati",
				pieces: "Nel tuo sistema",
				admin: "Amministrazione",
				contact: "Contatti",
				conversation: "Conversazioni",
				record: "Record",
				action: "Crea senza uscire da qui",
			},
			actions: {
				contact: { label: "+ contatto", hint: "crea una persona" },
				pipeline: { label: "+ pipeline", hint: "crea una pipeline" },
				conversation: { label: "+ conversazione", hint: "scrivi tu per primo" },
				agent: { label: "+ agente", hint: "crea un agente" },
			},
			prefix: {
				billing: "Fatturazione",
				settings: "Impostazioni",
				system: "Il tuo sistema",
			},
			/**
			 * AS DUAS ABAS DE "Seu sistema" que têm endereço próprio.
			 *
			 * Elas existem no `?aba=` desde sempre e não existiam em porta
			 * nenhuma: quem procura "histórico" não o achava, e o Ben só sabia
			 * levar a pessoa à primeira aba — dizia "liga em O que pode ter" e
			 * não tinha como abrir. A terceira (o que você TEM) fica de fora: é
			 * a que `/system` já abre.
			 */
			system: {
				could: { label: "Cosa puoi avere", hint: "componenti e pacchetti, un clic ciascuno" },
				history: { label: "Cronologia", hint: "ogni modifica, con annulla" },
			},
			billing: {
				credits: {
					label: "Crediti",
					hint: "quanta IA resta",
					terms:
						"crediti saldo quota bonus pacchetto comprare crediti ricarica ia bloccata " +
						"senza saldo quanto resta azzeramento del ciclo",
				},
				limits: {
					label: "Limiti",
					hint: "utenti, canali, GB, contatti",
					terms:
						"limite limiti utenti canali archiviazione gb spazio contatti attivi componente " +
						"aggiuntivo aggiuntivi comprare piu utenti alzare il limite",
				},
				invoices: {
					label: "Fatture",
					hint: "che cosa è stato addebitato, mese per mese",
					terms: "fattura fatture ricevuta storico dei pagamenti scadenza pagata in attesa scaduta",
				},
				consumption: {
					label: "Consumo",
					hint: "dove sono finiti i crediti",
					terms:
						"consumo estratto uso delle chiamate per origine errori durata media dove ho " +
						"speso i crediti storico dei consumi",
				},
			},
		},
	},
	de: {
		palette: {
			placeholder: "Springe zu, suche oder erstelle…",
			empty: "Nichts gefunden",
			searching: "suche…",
			emptyHint: "Versuche ein anderes Wort, oder eines davon",
			suggestions: "Versuche",
			footer: {
				move: "bewegen",
				open: "öffnen",
				close: "schließen",
				/** O tempo de execução no canto direito, como a V2 desenhava. */
				took: "{ms}ms",
			},
			terms: {
				screen: "maske ansicht board gebaut",
				funnel: "pipeline board kanban phasen karten",
			},
			counted: "{shown} von {total}",
			more: "{n} weitere zeigen",
			partial: "In {searched} von {active} Datentabellen dieses Workspace gesucht.",
			/** O que cada peça de "Seu sistema" é, na segunda linha do resultado. */
			piece: {
				entity: "Datentabelle",
				field: "Feld",
				view: "Bildschirm",
				component: "Eigener Bildschirm",
				pipeline: "Funnel",
				artifact: "Funktion",
				automation: "Automatisierung",
				report: "Bericht",
				agent: "Betreuer",
				of: "{kind} · {owner}",
				archived: "{name} · archiviert",
			},
			sections: {
				product: "Masken des Produkts",
				screens: "Eigene Bildschirme",
				funnels: "Pipelines",
				entities: "Datentabellen",
				pieces: "In Ihrem System",
				admin: "Verwaltung",
				contact: "Kontakte",
				conversation: "Konversationen",
				record: "Datensätze",
				action: "Erstellen, ohne hier wegzugehen",
			},
			actions: {
				contact: { label: "+ Kontakt", hint: "eine Person anlegen" },
				pipeline: { label: "+ Pipeline", hint: "eine Pipeline anlegen" },
				conversation: { label: "+ Konversation", hint: "zuerst selbst schreiben" },
				agent: { label: "+ Agent", hint: "einen Agenten anlegen" },
			},
			prefix: {
				billing: "Abrechnung",
				settings: "Einstellungen",
				system: "Ihr System",
			},
			/**
			 * AS DUAS ABAS DE "Seu sistema" que têm endereço próprio.
			 *
			 * Elas existem no `?aba=` desde sempre e não existiam em porta
			 * nenhuma: quem procura "histórico" não o achava, e o Ben só sabia
			 * levar a pessoa à primeira aba — dizia "liga em O que pode ter" e
			 * não tinha como abrir. A terceira (o que você TEM) fica de fora: é
			 * a que `/system` já abre.
			 */
			system: {
				could: { label: "Was Sie haben können", hint: "Add-ons und Pakete, je ein Klick" },
				history: { label: "Verlauf", hint: "jede Änderung, mit Rückgängig" },
			},
			billing: {
				credits: {
					label: "Credits",
					hint: "wie viel KI noch bleibt",
					terms:
						"credits guthaben kontingent bonus paket credits kaufen aufladen ki blockiert " +
						"kein guthaben wie viel bleibt zyklus zurucksetzen",
				},
				limits: {
					label: "Limits",
					hint: "Nutzer, Kanäle, GB, Kontakte",
					terms:
						"limit limits nutzer kanale speicher gb speicherplatz aktive kontakte add-on " +
						"add-ons mehr nutzer kaufen limit erhohen",
				},
				invoices: {
					label: "Rechnungen",
					hint: "was berechnet wurde, Monat für Monat",
					terms:
						"rechnung rechnungen beleg quittung zahlungsverlauf falligkeit bezahlt offen " +
						"uberfallig",
				},
				consumption: {
					label: "Verbrauch",
					hint: "wohin die Credits gingen",
					terms:
						"verbrauch abrechnung nutzung der aufrufe nach herkunft fehler durchschnittliche " +
						"dauer wofur habe ich credits ausgegeben verbrauchsverlauf",
				},
			},
		},
	},
} as const;

/**
 * O ESCOPO DA PALETA: as palavras dela, as do MENU e as das abas de Configurações.
 *
 * Um componente monta UM escopo local — o segundo `useI18n` é ignorado em
 * silêncio —, então este objeto é o dicionário inteiro que o ⌘K enxerga. O que
 * ele deixar de fora não vira erro: vira linha em branco.
 *
 * ── O DEFEITO QUE ISTO CONSERTA ───────────────────────────────────────────
 *
 * `nav-items.ts` é uma tabela de CHAVES, e as chaves das telas do produto e da
 * administração (`items.*.label`, `items.*.hint`) moram em `./nav` — o mesmo
 * arquivo que nomeia a lateral. A paleta resolvia `items.home.label` contra um
 * escopo que só tinha `palette.*` e `page`/`tabs`; `te` respondia `false`, o
 * `say` devolvia `undefined` e `drawDestinations` caía no `?? ""`. Resultado: as
 * seções "Telas do produto" e "Administração" desenhavam ÍCONE E NADA MAIS,
 * enquanto Cobrança e Configurações — cujas chaves estavam aqui — apareciam
 * escritas. Um destino sem nome não é procurável nem legível: a peneira compara o
 * rótulo desenhado, e o rótulo era a string vazia.
 *
 * As três origens não colidem em primeiro nível: `palette` é daqui, `groups`,
 * `nav`, `items` e `rail` vêm do menu, `page` e `tabs` vêm de Configurações.
 * Nenhuma sobrescreve a outra, e a ordem do espalhamento é irrelevante de
 * propósito — se um dia colidirem, é chave nova em arquivo errado.
 */
export const paletteScopeMessages = {
	en: { ...paletteMessages.en, ...navMessages.en, ...settingsMessages.en },
	"pt-BR": {
		...paletteMessages["pt-BR"],
		...navMessages["pt-BR"],
		...settingsMessages["pt-BR"],
	},
	el: { ...paletteMessages.el, ...navMessages.el, ...settingsMessages.el },
	es: { ...paletteMessages.es, ...navMessages.es, ...settingsMessages.es },
	fr: { ...paletteMessages.fr, ...navMessages.fr, ...settingsMessages.fr },
	it: { ...paletteMessages.it, ...navMessages.it, ...settingsMessages.it },
	de: { ...paletteMessages.de, ...navMessages.de, ...settingsMessages.de },
};
